import API_BASE_URL from "../../config";
import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import "./VotePage.css";
import ShareModal from "./ShareModal";
import CommentsPage from "./CommentsPage";
import confetti from "canvas-confetti";

const MAX_DESCRIPTION_LENGTH = 100;

const VotePage = () => {
  const [videos, setVideos] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState("");
  const [showMessage, setShowMessage] = useState(false);
  const [showCommentsPage, setShowCommentsPage] = useState(false);
  const [currentVideoId, setCurrentVideoId] = useState(null);
  const currentVideoRef = useRef(null);
  const history = useHistory();
const [currentVideoTitle, setCurrentVideoTitle] = useState("");

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/videosvoted`);
        if (!response.ok) throw new Error(`Error: ${response.status}`);
        const result = await response.json();

        const formattedVideos = result.videos.map((video) => ({
          ...video,
          votes: formatNumberWithCommas(video.votes),
        }));

        const sortedVideos = formattedVideos.sort(
          (a, b) => a.widget_number - b.widget_number
        );

        const uniqueVideos = sortedVideos.filter(
          (v, i, self) => i === self.findIndex((t) => t.id === v.id)
        );

        setVideos(uniqueVideos);
      } catch (error) {
        console.error("Failed to fetch videos:", error);
      }
    };

    fetchVideos();
  }, []);

  const checkIfUserHasVoted = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/userhasvoted`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("jwt")}`,
        },
      });
      if (response.ok) {
        const result = await response.json();
        return result.hasVoted;
      }
      return false;
    } catch (error) {
      console.error("Error checking if user has voted:", error);
      return false;
    }
  };

  const handleVote = async (videoId) => {
    if (!localStorage.getItem("jwt")) {
      history.push("/signup");
      return;
    }

    const hasVotedBefore = await checkIfUserHasVoted();

    try {
      const response = await fetch(`${API_BASE_URL}/vote`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("jwt")}`,
        },
        body: JSON.stringify({ video_id: videoId }),
      });

      if (response.ok) {
        const updatedVideos = await fetch(`${API_BASE_URL}/videosvoted`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
        }).then((res) => res.json());

        const formattedVideos = updatedVideos.videos.map((video) => ({
          ...video,
          votes: formatNumberWithCommas(video.votes),
        }));

        const sortedUpdatedVideos = formattedVideos.sort(
          (a, b) => a.widget_number - b.widget_number
        );

        const uniqueVideos = sortedUpdatedVideos.filter(
          (v, i, self) => i === self.findIndex((t) => t.id === v.id)
        );

        setVideos(uniqueVideos);
        const updatedVideo = uniqueVideos.find((v) => v.id === videoId);
        flip(
          document.querySelector(`[data-video-id='${videoId}']`),
          updatedVideo.votes
        );

        if (!hasVotedBefore) {
          setShowMessage(true);
          launchConfetti();
        }
      } else {
        const errorData = await response.json();
        alert(errorData.error || "Error voting.");
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handlePlay = (videoElement) => {
    if (currentVideoRef.current && currentVideoRef.current !== videoElement) {
      currentVideoRef.current.pause();
    }
    currentVideoRef.current = videoElement;
  };

 const handleShareClick = (video) => {
    const message = `Help me vote for "${video.title}" to show on MPT and the PBS app`;
    setCurrentVideoUrl(message); // ✅ this must be a string
    setShowShareModal(true);
  };

  const handleCommentClick = (videoId) => {
    setCurrentVideoId(videoId);
    setShowCommentsPage(true);
  };

  const flip = (flipCard, newNumber) => {
    const formattedNumber = formatNumberWithCommas(newNumber);
    const topHalf = flipCard.querySelector(".top");
    const startNumber = parseInt(
      topHalf.getAttribute("data-number").replace(/,/g, ""),
      10
    );
    if (newNumber === startNumber) return;

    const bottomHalf = flipCard.querySelector(".bottom");
    const topFlip = document.createElement("div");
    topFlip.classList.add("top-flip");
    const bottomFlip = document.createElement("div");
    bottomFlip.classList.add("bottom-flip");

    topFlip.setAttribute("data-number", startNumber);
    bottomHalf.setAttribute("data-number", startNumber);
    topFlip.setAttribute("data-number", startNumber);
    bottomFlip.setAttribute("data-number", formattedNumber);

    topFlip.addEventListener("animationstart", () => {
      topHalf.setAttribute("data-number", formattedNumber);
    });
    topFlip.addEventListener("animationend", () => {
      topFlip.remove();
    });
    bottomFlip.addEventListener("animationend", () => {
      bottomHalf.setAttribute("data-number", formattedNumber);
      bottomFlip.remove();
    });

    flipCard.append(topFlip, bottomFlip);
  };

  const formatNumberWithCommas = (number) => {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const launchConfetti = () => {
    const duration = 5000;
    const end = Date.now() + duration;

    (function frame() {
      confetti({
        particleCount: 8,
        spread: 70,
        origin: { y: 0.6 },
        colors: ["#ff4757", "#3742fa", "#2ed573", "#ff6b81"],
      });

      if (Date.now() < end) requestAnimationFrame(frame);
    })();
  };

const VideoDescription = ({ title, description }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getTruncatedText = (text, length) => {
    return text.length > length && !isExpanded
      ? text.slice(0, length - 7) + "..."
      : text;
  };

  return (
    <p className="description-text">
      <strong>{title}</strong>:{" "}
      <span>
        {getTruncatedText(description, MAX_DESCRIPTION_LENGTH)}{" "}
        {description.length > MAX_DESCRIPTION_LENGTH && (
          <span
            className="toggle-more-less"
            onClick={() => setIsExpanded((prev) => !prev)}
            style={{ color: "#007bff", cursor: "pointer" }}
          >
            {isExpanded ? "less" : "more"}
          </span>
        )}
      </span>
    </p>
  );
};
  return (
    <div className="vote-page">
      <div className="navbar">
        <img
          src="https://image.pbs.org/bento3-prod/mpt-bento-live-pbs/main/16a2473dc6_mpt_header_logo_medium3.png?resize=640x,no-scale-up"
          alt="MPT Logo"
          className="mpt-logo"
        />
        <button
          className="donate-button"
          onClick={() =>
            (window.location.href = "https://donate.mpt.org/mptv/donate-2")
          }
        >
          DONATE
        </button>
      </div>

 <div className="welcome-message">

          <div className="viewers-like-banner">
          <img
            src="../../image/mel2.jpg"
            alt="Viewers Like Me"
            className="banner-image"
          />

        </div>
 
Thank you to everyone that voted!
<br />
<br />
The most popular program, <strong>Mel Brooks: The Genius Entertainer </strong>will be shown on Maryland Public Television on November 1st @ 8:00pm.
<br />
<br />
Remember to download the PBS, so you can watch your favorite program.


        <div className="download-actions">
          <button
            className="app-button"
            onClick={() =>
              window.open("https://www.mpt.org/anywhere/pbs-app/", "_blank")
            }
          >
            Download the PBS App
          </button>
        </div>
      </div>

      <div className="vote-container">
        {videos.map((video) => (
          <div className="video-card" key={video.id}>
            <video
              className="video-player"
              controls
              onPlay={(e) => handlePlay(e.target)}
              poster={video.thumbnail_url}
            >
              <source src={video.s3_url} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            <div className="video-details">
              <div className="video-title">
                <VideoDescription
                  title={video.title}
                  description={video.description}
                />
              </div>
              <div className="video-info">
                <div className="vote-info">
                  <div className="vote-label">Votes:</div>
                  <div className="flip-card" data-video-id={video.id}>
                    <div
                      className="top"
                      data-number={formatNumberWithCommas(video.votes)}
                    ></div>
                    <div
                      className="bottom"
                      data-number={formatNumberWithCommas(video.votes)}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
            <div className="video-actions">
              <button
                className="vote-button"
                onClick={() => handleVote(video.id)}
              >
                VOTE
              </button>

<button
                className="share-button"
                onClick={() => handleShareClick(video)}
              >
                SHARE
              </button>

              <button
                className="discuss-button"
                onClick={() => handleCommentClick(video.id)}
              >
                COMMENT
              </button>
            </div>
          </div>
        ))}
      </div>

{showShareModal && (
        <div className="share-modal-overlay">
          <div className="share-modal">
            <h2>Share this video</h2>

            <button
              className="facebook-button"
              onClick={() => {
                const url = encodeURIComponent(
                  "https://viewerslikeme.mpt.org/"
                );
                window.open(
                  `https://www.facebook.com/sharer/sharer.php?u=${url}`,
                  "_blank"
                );
              }}
            >
              Facebook
            </button>

            <button
              className="twitter-button"
              onClick={() => {
                const text = encodeURIComponent(currentVideoUrl);
                window.open(
                  `https://twitter.com/intent/tweet?text=${text}`,
                  "_blank"
                );
              }}
            >
              X
            </button>

            <button
              className="copy-button"
              onClick={() => {
navigator.clipboard.writeText(`${currentVideoUrl}\nhttps://viewerslikeme.mpt.org/`);
                alert("Link copied!");
              }}
            >
              Copy Link
            </button>

            <button
              className="share-close-button"
              onClick={() => setShowShareModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {showCommentsPage && (
        <CommentsPage
          videoId={currentVideoId}
          currentUserId={localStorage.getItem("userId")}
          onClose={() => setShowCommentsPage(false)}
        />
      )}

      {showMessage && (
        <div className="message-container">
          <div className="confetti-container"></div>
          <div className="message">
            <img
              src="../../image/firstvotepopup.jpg"
              alt="First Vote Popup"
              style={{ maxWidth: "100%", height: "auto" }}
            />
            <div className="message-buttons">
              <button
                className="donate-button"
                onClick={() =>
                  window.open("https://donate.mpt.org/mptv/donate-2", "_blank")
                }
              >
                Donate
              </button>
              <button
                className="back-button"
                onClick={() => setShowMessage(false)}
              >
                Back
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VotePage;

